import {
  chainCommands,
  joinForward,
  selectNodeForward,
} from 'hubspot-prosemirror/pm/commands';
import {
  ResolvedPos,
  Slice,
  Node,
  NodeSpec,
  Fragment,
} from 'hubspot-prosemirror/pm/model';
import {
  EditorState,
  TextSelection,
  Transaction,
} from 'hubspot-prosemirror/pm/state';
import memoize from 'transmute/memoize';
import { FocusPlugin } from '../plugins/FocusPlugin';
import { ScrollViewPlugin } from '../plugins/ScrollViewPlugin';
import { getDomParser } from './domparser';
import { findNode } from './nodeUtils';
import { replaceHardBreaksWithParagraphs } from './replaceHardBreaksWithParagraphs';
import { makeLinkNode } from 'hubspot-prosemirror/prosemirror/plugins/link/LinkUtils';
import { encodeUrl } from 'hubspot-prosemirror/draft-transplant/utils/libUtils';
import { ensureUrlHasProtocol } from 'hubspot-prosemirror/draft-transplant/utils/libUtils';
import { buildDocumentTags } from './documentSchemaUtils';

const checkGroup = memoize((spec: NodeSpec, group: string) => {
  if (!spec.group) {
    return false;
  }
  return spec.group.split(' ').includes(group);
});

/**
 * Find the first direct child of the given node which is a member of
 * the given group, and return the position of that child. Does not
 * recurse into descendants.
 */
function findFirstNodeInGroup(node: Node, group: string): number | undefined {
  let result: number | undefined;
  node.forEach((descendant, pos) => {
    const isStillLookingForFirstNodeOfGroup = !result;
    if (
      isStillLookingForFirstNodeOfGroup &&
      checkGroup(descendant.type.spec, group)
    ) {
      result = result || pos;
    }
  });
  return result;
}

/**
 * Find the position of the first "footer" child of the given node.
 *
 * The main use case is the content of email reply histories, which are a
 * special node that should be ignored when we try to find the "end" of a
 * document. Users don't usually want to automatically move the cursor there.
 */
function findPosOfFooterNodes(node: Node): number | undefined {
  return findFirstNodeInGroup(node, 'footer');
}

// If is not passed in, selection will be set to the end of regular content
export const setTrSelectionPosition = (
  tr: Transaction,
  pos?: number | null
) => {
  try {
    let newSelection;
    if (pos) {
      const resolvedPos = tr.doc.resolve(pos);
      newSelection = TextSelection.near(resolvedPos);
      return tr.setSelection(newSelection);
    }
    const posBeforeFooterNodes = findPosOfFooterNodes(tr.doc);
    if (posBeforeFooterNodes !== undefined) {
      return tr.setSelection(
        TextSelection.near(tr.doc.resolve(posBeforeFooterNodes), -1)
      );
    } else {
      return tr.setSelection(TextSelection.atEnd(tr.doc));
    }
  } catch (e) {
    // return basic tr, no selection changes if we error out
    return tr;
  }
};

export function insertLink({
  htmlBody,
  editorState,
  from,
  to,
}: {
  htmlBody: string;
  editorState: EditorState;
  from: number;
  to: number;
}) {
  let tr = editorState.tr;
  const domparser = getDomParser(editorState.schema);
  const element = stringToDomNode(htmlBody);
  const parsedDoc = domparser.parse(element);
  const linkNode = parsedDoc.content.firstChild?.firstChild;
  if (linkNode) {
    tr = tr.replaceRangeWith(from, to, linkNode);
  }
  tr.setMeta(FocusPlugin, {
    focusView: true,
    focusViewPos: tr.mapping.map(to),
  });
  return tr;
}

/**
 * Parse a string of HTML safely and return the resulting DOM node
 * @param htmlString
 */
export function stringToDomNode(htmlString: string) {
  const domparser = new DOMParser();
  const doc = domparser.parseFromString(htmlString, 'text/html').body;
  return doc;
}

export function insertSignature({
  htmlBody,
  editorState,
}: {
  htmlBody: string;
  editorState: EditorState;
}) {
  const unsubscribeNode = findNode(
    editorState.doc,
    (n) => n.type.name === 'unsubscribe'
  );

  const brandingNode = findNode(
    editorState.doc,
    (n) => n.type.name === 'branding'
  );

  const insertPosition =
    unsubscribeNode?.pos ||
    brandingNode?.pos ||
    editorState.tr.doc.content.size;

  const signatureNode = editorState.schema.nodes.signature.create({
    signature: htmlBody,
  });
  const transaction = editorState.tr.insert(insertPosition, signatureNode);
  return editorState.apply(transaction);
}

export function buildMeetingTr({
  meetingURL,
  meetingName,
  editorState,
}: {
  meetingURL: string;
  meetingName: string;
  editorState: EditorState;
}) {
  let tr = editorState.tr;
  const marks = editorState.selection.$from.marks();
  const meetingsNode = editorState.schema.nodes.meetings.create(
    {
      meetingURL,
      meetingName,
    },
    null,
    marks
  );

  tr = tr
    .replaceSelection(new Slice(Fragment.fromArray([meetingsNode]), 0, 0))
    .setMeta(FocusPlugin, {
      focusView: true,
      focusViewPos: editorState.selection.$head,
    });

  return tr;
}

export function buildDocumentLinkTr({
  documentId,
  documentName,
  displayText,
  requireEmail,
  linkInfo,
  allowDownload,
  editorState,
}: {
  documentId: number;
  documentName: string;
  displayText: string;
  requireEmail: boolean;
  linkInfo: { url: string; text: string } | null;
  allowDownload: boolean;
  editorState: EditorState;
}) {
  let tr = editorState.tr;
  let documentLinkNode;
  if (linkInfo) {
    const { url, text } = linkInfo;
    const urlWithProtocol = ensureUrlHasProtocol(url);
    const encodedUrl = encodeUrl(urlWithProtocol);
    documentLinkNode = makeLinkNode(
      editorState.schema,
      encodedUrl,
      displayText || text,
      true,
      false,
      editorState.selection.$from.marks()
    );
  } else {
    const { documentTokenTag } = buildDocumentTags(
      documentId,
      requireEmail,
      allowDownload
    );
    const marks = editorState.selection.$from.marks();
    documentLinkNode = editorState.schema.nodes.documentLink.create(
      {
        documentId,
        documentName,
        displayText,
        requireEmail,
        tokenTag: `{{ ${documentTokenTag} }}`,
        allowDownload,
      },
      null,
      marks
    );
  }

  tr = tr
    .replaceSelection(new Slice(Fragment.fromArray([documentLinkNode]), 0, 0))
    .setMeta(FocusPlugin, {
      focusView: true,
      focusViewPos: editorState.selection.$head,
    });

  return tr;
}

export function buildDocumentPreviewTr({
  documentId,
  documentName,
  requireEmail,
  previewTitle,
  previewImage,
  description,
  linkInfo,
  includeLinkPreview,
  editorState,
  updateFromView = false,
}: {
  documentId: number;
  documentName: string;
  requireEmail: boolean;
  previewTitle: string;
  previewImage: string;
  description: string;
  includeLinkPreview: boolean;
  linkInfo: { url: string; text: string } | null;
  editorState: EditorState;
  updateFromView?: boolean;
}) {
  if (!includeLinkPreview) return editorState.tr;

  const { documentTokenTag } = buildDocumentTags(documentId, requireEmail);

  let tr = editorState.tr;
  const documentPreviewNode = editorState.schema.nodes.documentPreview.create({
    documentId,
    documentName,
    requireEmail,
    previewTitle,
    previewImage,
    description,
    tokenTag: linkInfo ? linkInfo.url : `{{ ${documentTokenTag} }}`,
  });

  const fragmentNodes = updateFromView
    ? [documentPreviewNode]
    : [documentPreviewNode, editorState.schema.nodes.paragraph.create()];

  tr = tr
    .replaceSelection(new Slice(Fragment.fromArray(fragmentNodes), 0, 0))
    .setMeta(FocusPlugin, {
      focusView: true,
      focusViewPos: editorState.selection.$head,
    });

  return tr;
}

export function insertContent({
  htmlBody,
  editorState,
  from,
  to,
}: {
  htmlBody: string;
  editorState: EditorState;
  from: number;
  to: number;
}) {
  const insertionTransactions = [];
  const tr = editorState.tr;
  const domparser = getDomParser(editorState.schema);
  const element = stringToDomNode(htmlBody);
  const snippetNode = domparser.parse(element, {
    preserveWhitespace: true,
  });
  const shouldAdjustFrom = from === 1;

  tr.replaceRangeWith(shouldAdjustFrom ? from - 1 : from, to, snippetNode);

  insertionTransactions.push(tr);
  let newState = editorState.apply(tr);

  if (!shouldAdjustFrom) {
    chainCommands(joinForward, selectNodeForward)(newState, (cmdTr) => {
      insertionTransactions.push(cmdTr);
      newState = newState.apply(cmdTr);
    });
  }

  const adjustedPositon = insertionTransactions.reduce(
    (position: number, transaction) => transaction.mapping.map(position),
    to + 1
  );

  const selectionAdjustedTr = setTrSelectionPosition(
    newState.tr,
    adjustedPositon
  )
    .setMeta(ScrollViewPlugin, {
      scrollToPos: from,
    })
    .setMeta(FocusPlugin, {
      focusView: true,
      focusViewPos: adjustedPositon,
    });

  return newState.apply(selectionAdjustedTr);
}

/**
 * If a slice has only one child node, unwrap the child and return it. Otherwise,
 * return null.
 *
 * @param slice
 * @returns the solitary child of a slice, or null if there isn't one
 */
function sliceSingleNode(slice: Slice) {
  return slice.openStart === 0 &&
    slice.openEnd === 0 &&
    slice.content.childCount === 1
    ? slice.content.firstChild
    : null;
}

/**
 * Checks if HTML content contains text-align styling.
 * Used to determine whether to use insertContent or insertSlice for proper alignment handling.
 *
 * @param htmlBody - The HTML string to check
 * @returns true if text-align is found in the HTML
 */
export function hasAlignmentInHtml(htmlBody: string): boolean {
  /**
   * What it matches:
   * - style="text-align: right"
   * - style='text-align:center'
   * - style = "color: red; text-align: left; font-size: 14px"
   * - STYLE="TEXT-ALIGN:right" (case insensitive)
   */
  return /style\s*=\s*["|'].*text-align\s*:/i.test(htmlBody);
}

/**
 * Checks if a resolved position is at the beginning of a paragraph.
 * Used to determine insertion behavior for snippets with alignment.
 *
 * @param $pos - The resolved position to check
 * @returns true if the position is at the start of a paragraph (parentOffset === 0)
 */
export function isAtParagraphStart($pos: ResolvedPos): boolean {
  return $pos.parentOffset === 0;
}

/**
 * Insert a piece of HTML content as a Slice, replacing the range given as $to through $from.
 *
 * As opposed to {@see insertContent }, this will not cause content to split blocks or insert
 * new paragraphs. Inserted content will behave as if it were pasted into the document.
 */
export function insertSlice({
  htmlBody,
  editorState,
  $from,
  $to,
}: {
  htmlBody: string;
  editorState: EditorState;
  $from: ResolvedPos;
  $to: ResolvedPos;
}) {
  const dom = stringToDomNode(htmlBody);
  replaceHardBreaksWithParagraphs(dom);
  const slice = getDomParser(editorState.schema).parseSlice(dom, {
    preserveWhitespace: true,
  });
  if (!slice) return editorState;
  const tr = editorState.tr.setSelection(new TextSelection($from, $to));
  const singleNode = sliceSingleNode(slice);
  if (singleNode) {
    tr.replaceSelectionWith(singleNode, false);
  } else {
    tr.replaceSelection(slice);
  }
  tr.setMeta(ScrollViewPlugin, {
    scrollToPos: $from.pos,
  });
  tr.setMeta(FocusPlugin, {
    focusView: true,
  });
  return editorState.apply(tr);
}

function updateExistingToken({
  tr,
  nodePos,
  tokenKey,
  displayText,
  tokenStyles,
  defaultTokenValue,
  tokenMeta,
}: {
  tr: Transaction;
  nodePos: number;
  tokenKey: string;
  displayText?: string;
  tokenStyles?: any;
  defaultTokenValue?: string;
  tokenMeta?: Record<string, unknown> | null;
}) {
  const node = tr.doc.nodeAt(nodePos);
  if (node?.type.name !== 'personalization_token') {
    return tr;
  }

  const newAttrs = {
    tokenKey,
    displayText,
    tokenStyles,
    defaultTokenValue,
    tokenMeta,
  };
  return tr.setNodeMarkup(nodePos, undefined, newAttrs).setMeta(FocusPlugin, {
    focusView: true,
    focusViewPos: nodePos + 1,
  });
}

function insertNewToken({
  tr,
  editorState,
  tokenKey,
  displayText,
  tokenStyles,
  defaultTokenValue,
  tokenMeta,
}: {
  tr: Transaction;
  editorState: EditorState;
  tokenKey: string;
  displayText?: string;
  tokenStyles?: any;
  defaultTokenValue?: string;
  tokenMeta?: Record<string, unknown> | null;
}) {
  const marks = editorState.selection.$from.marks();
  const personalizationTokenNode =
    editorState.schema.nodes.personalization_token.create(
      {
        tokenKey,
        displayText,
        tokenStyles,
        defaultTokenValue,
        tokenMeta,
      },
      null,
      marks
    );

  return tr
    .replaceSelection(
      new Slice(Fragment.fromArray([personalizationTokenNode]), 0, 0)
    )
    .setMeta(FocusPlugin, {
      focusView: true,
      focusViewPos: editorState.selection.$head,
    });
}

export function buildPersonalizationTokenTr({
  tokenKey,
  displayText,
  tokenStyles,
  defaultTokenValue,
  tokenMeta,
  editorState,
  nodePos,
}: {
  tokenKey: string;
  displayText?: string;
  tokenStyles?: any;
  defaultTokenValue?: string;
  tokenMeta?: Record<string, unknown> | null;
  editorState: EditorState;
  nodePos?: number;
}) {
  const tr = editorState.tr;
  const shouldInsertNewToken = nodePos === undefined;

  if (shouldInsertNewToken) {
    return insertNewToken({
      tr,
      editorState,
      tokenKey,
      displayText,
      tokenStyles,
      defaultTokenValue,
      tokenMeta,
    });
  }

  return updateExistingToken({
    tr,
    nodePos,
    tokenKey,
    displayText,
    tokenStyles,
    defaultTokenValue,
    tokenMeta,
  });
}

export function insertPersonalizationTokenAtRange({
  from,
  to,
  tokenKey,
  displayText,
  tokenStyles,
  defaultTokenValue,
  tokenMeta,
  editorState,
}: {
  from: number;
  to: number;
  tokenKey: string;
  displayText?: string;
  tokenStyles?: any;
  defaultTokenValue?: string;
  tokenMeta?: Record<string, unknown> | null;
  editorState: EditorState;
}) {
  const tr = editorState.tr;
  const marks = editorState.doc.resolve(from).marks();
  const personalizationTokenNode =
    editorState.schema.nodes.personalization_token.create(
      {
        tokenKey,
        displayText,
        tokenStyles,
        defaultTokenValue,
        tokenMeta,
      },
      null,
      marks
    );

  return tr
    .replaceWith(from, to, personalizationTokenNode)
    .setMeta(FocusPlugin, {
      focusView: true,
      focusViewPos: from + 1,
    });
}
